import { useCallback, useEffect, useState } from "react";

import { safeLocalStorage } from "~/lib/utils/safe-storage";

export const FSA_SPENDER_BALANCE_STORAGE_KEY = "hec_fsa_spender_balance";

// Legacy spender: a 0 at the prompt is a real balance, but restore ignores 0.
const restorable = (cents: number) => Number.isSafeInteger(cents) && cents > 0;

/**
 * The shopper's FSA balance in cents, null when none is set; `set` of a
 * negative or unsafe value clears.
 * With `persist` it is kept in localStorage and read after mount, so SSR and
 * hydration see null; `restored` is false until that read has run.
 */
export function useBalance({ persist = false }: { persist?: boolean } = {}): [
  balance: number | null,
  set: (cents: number) => void,
  clear: () => void,
  restored: boolean,
] {
  const [balance, setBalance] = useState<number | null>(null);
  const [restored, setRestored] = useState(!persist);

  useEffect(() => {
    if (!persist) return;
    const raw = safeLocalStorage.getItem(FSA_SPENDER_BALANCE_STORAGE_KEY) ?? "";
    const stored = /^\d+$/.test(raw) ? Number(raw) : NaN;
    if (restorable(stored)) setBalance(stored);
    setRestored(true);
  }, [persist]);

  const clear = useCallback(() => {
    setBalance(null);
    if (persist) safeLocalStorage.removeItem(FSA_SPENDER_BALANCE_STORAGE_KEY);
  }, [persist]);

  const set = useCallback(
    (cents: number) => {
      if (!Number.isSafeInteger(cents) || cents < 0) return clear();
      setBalance(cents);
      if (persist)
        safeLocalStorage.setItem(
          FSA_SPENDER_BALANCE_STORAGE_KEY,
          String(cents),
        );
    },
    [persist, clear],
  );

  return [balance, set, clear, restored];
}
